import { useState } from 'react';
import {
  useLicenses,
  useSetActiveLicense,
  useCreateLicense,
  useUpdateLicense,
  useDeleteLicense,
} from '../hooks/use-licenses';
import { useLicensesStore, useLicensesActions } from '@/features/licenses/stores/license-store';
import { CustomLicenseModal } from './custom-license-modal';
import { License, LicenseCustomMetadata } from '@/features/licenses/types';
import {
  Button,
  ChoiceRadio,
  ChoiceRadioItem,
  Skeleton,
  Item,
  ItemContent,
  ItemTitle,
  ItemDescription,
  cn,
} from '@design-system';
import { Pencil, Trash2, Plus, UserCircle2 } from 'lucide-react';
import { DestructiveActionDialog } from '@/components/DestructiveActionDialog';

interface LicenseListProps {
  hideHeader?: boolean;
}

export function LicenseList({ hideHeader = false }: LicenseListProps = {}) {
  const { data, isLoading } = useLicenses();
  const { mutate: setActive, isPending: isSettingActive } = useSetActiveLicense();
  const { mutate: createLicense, isPending: isCreating } = useCreateLicense();
  const { mutate: updateLicense, isPending: isUpdating } = useUpdateLicense();
  const { mutate: deleteLicense, isPending: isDeleting } = useDeleteLicense();

  const { isModalOpen, editingLicenseId } = useLicensesStore();
  const { openCreateModal, openEditModal, closeModal } = useLicensesActions();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [licenseToDelete, setLicenseToDelete] = useState<License | null>(null);

  const licenses = data?.licenses || [];

  const advisorLicense = licenses.find(l => l.ownerType === 'ADVISOR');
  const wagLicense = licenses.find(l => l.ownerType === 'WAG');
  const customLicenses = licenses.filter(l => l.ownerType === 'CUSTOM');

  const CREATE_ADVISOR_ID = 'create-advisor';
  const CREATE_WAG_ID = 'create-wag';

  const advisorRadioValue = advisorLicense?.licenseId || CREATE_ADVISOR_ID;
  const wagRadioValue = wagLicense?.licenseId || CREATE_WAG_ID;

  // Default to Advisor if no active license is set
  const selectedLicenseId = data?.activeLicenseId || advisorRadioValue;

  const editingLicense = licenses.find(l => l.licenseId === editingLicenseId) || null;

  const handleRadioChange = (value: string) => {
    if (value === CREATE_ADVISOR_ID) {
      createLicense({ ownerType: 'ADVISOR' });
    } else if (value === CREATE_WAG_ID) {
      createLicense({ ownerType: 'WAG' });
    } else {
      setActive({ licenseId: value });
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, license: License) => {
    e.preventDefault();
    e.stopPropagation();
    setLicenseToDelete(license);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (licenseToDelete) {
      deleteLicense(licenseToDelete.licenseId);
      setLicenseToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>

        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div
              key={i}
              className="flex items-center space-x-3 rounded-lg border p-4"
            >
              <Skeleton className="h-5 w-5 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-48" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const isAnyActionPending = isSettingActive || isCreating || isUpdating || isDeleting;

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        {!hideHeader && (
          <div className="space-y-1">
            <h3 className="text-lg font-semibold text-gray-900">Illustration Presenter</h3>
            <p className="text-muted-foreground text-sm">
              Choose who appears as the presenter on policy illustrations.
            </p>
          </div>
        )}

        <ChoiceRadio
          value={selectedLicenseId}
          onValueChange={handleRadioChange}
          disabled={isAnyActionPending}
        >
          {/* Main Options */}
          <ChoiceRadioItem
            value={advisorRadioValue}
            title="I am licensed to sell insurance"
            description="Use your name and organization details from your advisor profile."
          />

          <ChoiceRadioItem
            value={wagRadioValue}
            title="Use Wealth Assurance Group as entity"
            description="Show your name with Wealth Assurance Group organization details."
          />

          {/* Item to Trigger if no custom licenses yet */}
          {customLicenses.length === 0 && (
            <Item
              variant="outline"
              className={cn('hover:bg-muted cursor-pointer rounded-lg transition-all', {
                'pointer-events-none opacity-50': isAnyActionPending,
              })}
              onClick={() => !isAnyActionPending && openCreateModal()}
            >
              <div className="bg-primary/10 text-primary flex size-10 shrink-0 items-center justify-center rounded-full">
                <Plus className="size-5" />
              </div>
              <ItemContent>
                <ItemTitle>Use a different entity/person/BGA</ItemTitle>
                <ItemDescription>
                  Add a custom presenter with specific business details.
                </ItemDescription>
              </ItemContent>
            </Item>
          )}

          {/* Custom licenses list */}
          {customLicenses.length > 0 && (
            <div className="mt-6 border-t pt-4">
              <div className="flex items-center justify-between pb-4">
                <h4 className="text-sm font-semibold text-gray-900">Custom Presenters</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    openCreateModal();
                  }}
                  className="h-8 px-2 text-xs font-medium"
                  disabled={isAnyActionPending}
                >
                  <Plus className="mr-1 size-3" /> Add Another
                </Button>
              </div>

              <div className="space-y-2">
                {customLicenses.map(license => {
                  const metadata = license.metadata as LicenseCustomMetadata;
                  const title = license.displayName || `${metadata.firstName} ${metadata.lastName}`;
                  const organization =
                    metadata.organizationDisplayName || metadata.organizationName;
                  const description = organization
                    ? `${metadata.firstName} ${metadata.lastName} • ${organization}`
                    : `${metadata.firstName} ${metadata.lastName}`;

                  return (
                    <div
                      key={license.licenseId}
                      className="group relative flex items-center gap-2"
                    >
                      <ChoiceRadioItem
                        value={license.licenseId}
                        title={title}
                        description={description}
                        className="flex-1"
                      />
                      <div className="absolute right-12 flex gap-1 opacity-100 transition-opacity lg:opacity-0 lg:group-hover:opacity-100">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8"
                          disabled={isAnyActionPending}
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            openEditModal(license.licenseId);
                          }}
                        >
                          <Pencil className="size-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10 size-8"
                          disabled={isAnyActionPending}
                          onClick={e => handleDeleteClick(e, license)}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </ChoiceRadio>
      </div>

      <CustomLicenseModal
        open={isModalOpen}
        onClose={closeModal}
        onSave={(metadata, displayName) => {
          if (editingLicenseId) {
            updateLicense({ licenseId: editingLicenseId, data: { metadata, displayName } });
          } else {
            createLicense({ ownerType: 'CUSTOM', metadata, displayName });
          }
          closeModal();
        }}
        isLoading={isCreating || isUpdating}
        editingLicense={editingLicense}
      />

      <DestructiveActionDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Presenter"
        description={
          <>
            Are you sure you want to delete{' '}
            <span className="font-medium">
              {licenseToDelete?.displayName ||
                `${(licenseToDelete?.metadata as LicenseCustomMetadata)?.firstName} ${(licenseToDelete?.metadata as LicenseCustomMetadata)?.lastName}`}
            </span>
            ? This action cannot be undone.
          </>
        }
        confirmButtonText="Delete"
      />
    </div>
  );
}
